import { Fragment } from 'react'
import styled from 'styled-components'
import Script from 'next/script'

import Accordion from '../../src/components/Accordion'
import RichText from '../../src/components/RichText'
import { media } from '../../src/utils/style-utils'
import CardContainer from '../../src/components/CardContainer'
import { CardPadding } from '../../src/components/Card'
import useLocalization from '../../src/utils/hooks/use-localization'
import { Arrow } from '../../src/components/icons'
import { generateSchema } from '../../src/utils/seo-helpers'
import { QnASlice, QnASliceDefault } from '../../prismicio-types'
import { checkLinkUrl, getLinkUrl } from '../../src/utils/ts-helpers'
import { backgroundColors } from '../../src/styles/backgroundColors'

const QnA = ({ slice }: { slice: QnASlice }) => {
	const questionschema = generateSchema(slice.items)
	const fullWidth = slice.variation === 'fullWidth'
	const { translate } = useLocalization()
	const hasConsolidatePageLink = 'consolidate_page' in slice.primary && checkLinkUrl(slice.primary?.consolidate_page) // !!slice.primary?.consolidate_page?.url
	const backgroundColor = backgroundColors(slice.primary.background_color ?? 'Blue (100)')

	const isLightColor = backgroundColor === backgroundColors('Blue (100)')

	return (
		<Container options={{ backgroundColor }}>
			{
				// ! This is experimental in the sense that it's uncertain what impact
				// ! it might have to run this on run time, as opposed to build tim
			}
			<Script
				id={`qna-${slice.id}`}
				type='application/ld+json'
				dangerouslySetInnerHTML={{ __html: JSON.stringify(questionschema, null, '\t') }}
			/>
			{!fullWidth && !fullWidth && <Heading>{slice.primary.title}</Heading>}
			<AccordionContainer fullWidth={fullWidth}>
				<Divider isLightColor={isLightColor} />
				{slice.items.map((item, index) => (
					<Fragment key={index}>
						<Accordion
							key={index}
							heading={item.question ?? ''}
						>
							<RichText content={item.answer} />
						</Accordion>
						<Divider isLightColor={isLightColor} />
					</Fragment>
				))}
				{hasConsolidatePageLink && (
					<ConsolidateAnchor href={getLinkUrl((slice as QnASliceDefault).primary.consolidate_page)}>
						{translate('qna.seeMoreQuestions')}
						<Arrow />
					</ConsolidateAnchor>
				)}
			</AccordionContainer>
		</Container>
	)
}

const ConsolidateAnchor = styled.a`
	display: flex;
	align-self: flex-end;
	align-items: center;
	justify-content: flex-end;
	gap: var(--spacer-xs);
	font-family: var(--font-atyp-display);
	font-size: 1.5rem;
	font-weight: 400;
	color: var(--deep-blue) !important;
	cursor: pointer;
	padding-top: var(--spacer-md);

	&:hover {
		text-decoration: underline;
	}

	${media.phone`
		padding-top: var(--spacer-xs);
	`};

	${media.desktopHuge`
		font-size: 3rem;
		line-height: 3rem;
	`}
`

const Divider = styled.hr<{ isLightColor: boolean }>`
	border: 0;
	border-bottom: 1px solid ${(p) => (p.isLightColor ? 'var(--grey)' : 'var(--black)')};
`

const Container = styled(CardContainer)`
	padding-top: ${CardPadding};
	padding-bottom: ${CardPadding};

	a,
	a > strong {
		color: ${(p) => (p.options.backgroundColor === 'var(--orange)' ? 'var(--off-white)' : 'var(--orange)')};
	}

	${media.phone`
		row-gap: var(--spacer-xs);
	`}
`
const AccordionContainer = styled.div<{ fullWidth?: boolean }>`
	grid-column: ${(p) => (p.fullWidth ? '3 / span 6' : '6 / -2')};

	${media.tablet`
		grid-column: 2 / -2;
		margin-top: var(--spacer-md);
	`}

	${media.phone`
		margin-top: 0;
	`}
`

const Heading = styled.h2`
	grid-column: 2 / span 3;
	line-height: 110%;
	font-weight: 500;
	padding: 0;
	margin: 0;
	max-width: 15ch;

	white-space: break-spaces;

	${media.tablet`
		grid-column: 2 / -2;
	`}
`

export default QnA
